<template>
  <login-base-card :loading="loading" class="parent">
    <div v-if="!loading" class="content">
        <h2 class="title">
          Olá, boas-vindas!
        </h2>
        <p class="subtitle">
          Entre na sua conta Parcelex.
        </p>
      <form class="form" @submit.prevent="handleSubmit">
          <app-input
            class="form_input"
            label="CPF"
            v-model="cpfInputValue"
            placeholder="11 dígitos, sem traços e pontos"
            @input="handleCPFInput(...arguments)"
            :errorMessage="cpfForm.currentMessage"
            v-mask="'###.###.###-##'"
            :inputmode="'numeric'"
          />

          <app-button
            color="primary" @click="handleSubmit" class="form_button">
            Continuar
          </app-button>
        <div class="form_footer">
          <Link
            redirectRouter="/login/verificacao?origin=signIn"
            text="Não tenho uma conta"
          />
        </div>
      </form>
    </div>
    <div v-else class="loader">
        <loading-spinner class="loader-spinner" />
        <span class="gray-500"> Carregando Informações </span>
    </div>
  </login-base-card>
</template>

<script>
import { mask } from 'vue-the-mask';
import { mapActions, mapGetters, mapState } from 'vuex';
import stringMixin from '@/mixins/string';
import LoginBaseCard from '@/feature/login/components/LoginBaseCard.vue';
import Link from '@/components/common/Link.vue';
import AppInput from '../../../components/ui/AppInput.vue';
import AppButton from '../../../components/ui/AppButton.vue';
import utilMixin from '../../../mixins/util';
import LoadingSpinner from '../../paymentLink/components/LoadingSpinner.vue';
import ParcelexApi from '../../../services/parcelex';

export default ({
  name: 'LoginCard',
  components: {
    AppInput,
    AppButton,
    LoginBaseCard,
    LoadingSpinner,
    Link,
  },
  mixins: [stringMixin],
  directives: {
    mask,
  },
  data: () => ({
    cpfInputValue: '',
    loading: false,
    cpfForm: {
      isValid: false,
      errorMessage: 'CPF inválido',
      currentMessage: '',
    },
  }),
  computed: {
    ...mapState('login', ['user']),
    ...mapState('tracking', ['trackId']),
    ...mapGetters({ getTrackId: 'tracking/getTrackId' }),
  },
  methods: {
    ...mapActions('tracking', ['setTheTrackId']),
    ...mapActions('login', ['setUserData', 'getLogin']),
    handleCPFInput(value) {
      if (!value) return;
      this.setUserData({ field: 'cpf', value });
      this.cpfForm.isValid = utilMixin.methods.isValidCpf(value);
      if (this.cpfForm.currentMessage !== '') this.cpfForm.currentMessage = '';
    },
    async handleSubmit() {
      if (!this.cpfForm.isValid) {
        this.cpfForm.currentMessage = this.cpfForm.errorMessage;
      } else {
        this.loading = true;
        const { success } = await this.getLogin();
        this.loading = false;
        if (success) {
          this.$router.push('login/acesso');
          this.$emit('successLogin');
        }
      }
    },
    async sendEvent({ action, data }) {
      ParcelexApi.post('public/event/send', {
        action,
        source: 'login',
        origin: window.location.href.split('?')[0],
        data,
      }).catch((e) => console.error(e));
    },
  },
  created() {
    const { cpf, trackId } = this.$route.query;
    this.setTheTrackId(trackId);
    if (cpf) {
      if (cpf.length === 11) this.cpfInputValue = this.cpfFilter(cpf);
      else this.cpfInputValue = cpf;
    }
  },
  async mounted() {
    if (this.trackId) {
      this.sendEvent({
        action: 'visualizou',
        data: {
          trackId: this.trackId,
          ctaSignInShowed: true,
        },
      });
    }
  },
});
</script>

<style lang="scss" scoped>
  *, *::before, *::after {
    box-sizing: border-box;
  }

  .parent {
    min-height: 24.188rem;
    transition: all 0.3s;

    .content {
      animation-name:enter;
      animation-duration:0.3s;

      .title {
        margin-top: 1.5rem;
      }

      .subtitle {
        margin-top: 0.5rem;
        color: var(--color-gray-500);
      }

      .form {
        margin-top: 1.25rem;

        &_input {
          margin-bottom: 1.25rem;

          > ::v-deep input {
            display: block;
            width: 100%;
            border-radius: 0.375rem;
            border-color: var(--color-gray-300);
            padding: 8px 12px;
            background-color: white;
            height: 56px;

            &:focus {
              border-color: var(--color-primary);
            }

            @media (min-width: 1000px) {
              height: 2.625rem;
            }
          }

          > ::v-deep .input-error-message {
              padding-top: 0.25rem;
            }
        }

        > label {
          display: inline-block;
        }

        &_button {
          display: flex;
          justify-content: center;
          align-items: center;
          line-height: 1.5rem;
          font-weight: 600;
          cursor: pointer;
          width: 100%;
          color: var(--white);
          background-color: var(--color-primary);
          border-radius: 0.375rem;
          border-color: transparent;
          height: 3.5rem;

          &:hover {
            background-color: var(--color-primary-600) !important;
            border-color: transparent;
          }

          @media (min-width: 1000px) {
            height: 2.625rem;
          }
        }

        &_footer {
          display: flex;
          justify-content: center;
          margin-top: 1.25rem;

          > a {
            width: fit-content;

            &:hover {
              color: var(--primary);
              text-decoration: underline;
            }
          }
        }
      }
    }

    &[loading] {
      min-height: 20.75rem;

      @media (max-width: $sm) {
        width: -webkit-fill-available;
        max-width: 28rem;
      }

      .loader {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        animation-name:enter;
        animation-duration:0.3s;
        height: 14.25rem;

        > span {
          @extend .body-regular;
          font-size: 0.875rem;
          margin-top: 0.75rem;
        }
      }
    }
  }

  @keyframes enter {
    0% {
      opacity:0;
    }

    100% {
      opacity:1;
    }
  }
</style>
